import React, { useEffect } from "react"
import clsx from "clsx"
import MyModal from "@common-utils/MyModal"
import { EditableRow, PreviewState } from "../../types"

interface Props {
     previewFile: PreviewState | null
     onClose: () => void
     mobileView: "preview" | "form"
     setMobileView: (view: "preview" | "form") => void
     updateRow: (id: string, field: keyof EditableRow, value: any) => void
     consultants: any
     onNavigate: (index: number) => void
     navInfo: { prevIdx: number; nextIdx: number; current: number; total: number }
}

const Step3ExtractionVerificationModal = ({
                                               previewFile, onClose, updateRow, consultants, onNavigate, navInfo, mobileView, setMobileView
                                          }: Props) => {

     useEffect(() => {
          const handleKeyDown = (e: KeyboardEvent) => {
               if (e.key === "ArrowLeft" && navInfo.prevIdx !== -1) onNavigate(navInfo.prevIdx);
               if (e.key === "ArrowRight" && navInfo.nextIdx !== -1) onNavigate(navInfo.nextIdx);
          };
          window.addEventListener("keydown", handleKeyDown);
          return () => window.removeEventListener("keydown", handleKeyDown);
     }, [navInfo, onNavigate]);

     if (!previewFile) return null

     // Calcul dynamique du total HT (Prestation + Frais)
     const calculatedTotalHt = (Number(previewFile.rowData.prestationHt) || 0) + (Number(previewFile.rowData.fraisHt) || 0)

     return (
          <MyModal
               show={!!previewFile}
               onCloseCallback={onClose}
               fullscreen={true}
               noPadding={true}
               // Header : Navigation + Titre + Bouton Terminer (Desktop)
               title={
                    <div className="d-flex align-items-center justify-content-between w-100 pe-lg-15">
                         <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center me-6">
                                   <button
                                        className="btn btn-icon btn-xs btn-active-color-primary"
                                        disabled={navInfo.prevIdx === -1}
                                        onClick={() => onNavigate(navInfo.prevIdx)} // Correctly passes index
                                   >
                                        <i className="fas fa-chevron-left fs-9"></i>
                                   </button>
                                   <span className="fw-boldest fs-8 px-4 text-primary">
                                        {navInfo.current} / {navInfo.total}
                                   </span>
                                   <button
                                        className="btn btn-icon btn-xs btn-active-color-primary"
                                        disabled={navInfo.nextIdx === -1}
                                        onClick={() => onNavigate(navInfo.nextIdx)} // Correctly passes index
                                   >
                                        <i className="fas fa-chevron-right fs-9"></i>
                                   </button>
                              </div>

                              <div className="d-flex flex-column">
                                   <span className="text-gray-400 fw-bold fs-9 text-uppercase lh-1 mb-1">
                                        {previewFile.label || 'Facture Presta'}
                                   </span>
                                   <span className="text-gray-900 fw-boldest fs-6 lh-1 text-truncate" style={{ maxWidth: "180px" }}>
                                        {previewFile.rowData.consultantName || previewFile.name}
                                   </span>
                              </div>
                         </div>
                    </div>
               }
               titleMiddle={
                    <div className="d-lg-none">
                         <div className="btn-group shadow-sm bg-body">
                              <button
                                   className={clsx("btn btn-sm btn-icon", mobileView === "preview" ? "btn-primary" : "btn-active-light-primary")}
                                   onClick={() => setMobileView("preview")}
                              >
                                   <i className="fas fa-eye fs-6"></i>
                              </button>
                              <button
                                   className={clsx("btn btn-sm btn-icon", mobileView === "form" ? "btn-primary" : "btn-active-light-primary")}
                                   onClick={() => setMobileView("form")}
                              >
                                   <i className="fas fa-edit fs-6"></i>
                              </button>
                         </div>
                    </div>
               }
          >
               <div className="d-flex flex-column h-100 bg-gray-100">
                    <div className="d-flex flex-grow-1 flex-column flex-lg-row overflow-hidden" style={{ minHeight: 0 }}>

                         {/* GAUCHE : VISUALISATION DU DOCUMENT */}
                         <div className={clsx(
                              "flex-grow-1 bg-gray-300 position-relative h-100",
                              mobileView !== "preview" && "d-none d-lg-block",
                         )} style={{ minWidth: "60%" }}>
                              {previewFile.type === "application/pdf" ? (
                                   <iframe src={previewFile.url} width="100%" height="100%" title="Invoice" style={{ border: "none" }} />
                              ) : (
                                   <div className="d-flex align-items-center justify-content-center h-100 p-5">
                                        <img src={previewFile.url} alt="Invoice" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                                   </div>
                              )}
                         </div>

                         {/* DROITE : FORMULAIRE DE SAISIE */}
                         <div className={clsx(
                              "bg-body h-100 d-flex flex-column w-lg-450px border-start shadow-sm",
                              mobileView !== "form" && "d-none d-lg-block",
                         )} style={{ width: "100%", minWidth: "350px" }}>

                              <div className="flex-grow-1 overflow-auto p-8 p-lg-10">
                                   <div className="mb-8 d-flex align-items-center">
                                        <div className="symbol symbol-40px me-4">
                                             <div className="symbol-label bg-light-primary">
                                                  <i className="fas fa-check-double text-primary fs-4"></i>
                                             </div>
                                        </div>
                                        <div>
                                             <h4 className="fw-bolder text-gray-900 mb-0">Vérification des données</h4>
                                             <p className="text-muted fs-7 mb-0">Corrigez les montants extraits si nécessaire.</p>
                                        </div>
                                   </div>

                                   <div className="d-flex flex-column gap-8">
                                        {/* Consultant */}
                                        <div className="fv-row">
                                             <label className="form-label fw-bold fs-7 text-gray-700 text-uppercase">Consultant</label>
                                             <select
                                                  className="form-select form-select-solid border"
                                                  value={previewFile.rowData.selectedConsultantId || ""}
                                                  onChange={(e) => updateRow(previewFile.rowData.id, "selectedConsultantId", e.target.value ? Number(e.target.value) : null)}
                                             >
                                                  <option value="">-- Sélectionner --</option>
                                                  {consultants?.data.map((item: any) => (<option key={item.consultant.id} value={item.consultant.id}>{item.consultant.prenomNom}</option>))}
                                             </select>
                                        </div>

                                        {/* Temps travaillé */}
                                        <div className="fv-row">
                                             <label className="form-label fw-bold fs-7 text-gray-700 text-uppercase">Temps travaillé (Jours)</label>
                                             <input
                                                  type="number"
                                                  step="0.5"
                                                  className="form-control form-control-solid border text-center fw-bolder fs-4"
                                                  value={previewFile.rowData.nbJoursTravailles ?? ""}
                                                  onChange={(e) => updateRow(previewFile.rowData.id, "nbJoursTravailles", e.target.value === "" ? "" : Number(e.target.value))}
                                             />
                                        </div>

                                        {/* Section Prestation */}
                                        <div className="bg-light-primary bg-opacity-10 p-5 rounded border border-primary border-dashed">
                                             <h5 className="text-primary fs-7 fw-bolder text-uppercase mb-4">Détails Prestation</h5>
                                             <div className="row g-5">
                                                  <div className="col-6">
                                                       <label className="form-label fw-bold fs-8 text-gray-600">Montant HT (€)</label>
                                                       <input
                                                            type="number"
                                                            className="form-control form-control-solid border fw-bold"
                                                            value={previewFile.rowData.prestationHt ?? ""}
                                                            onChange={(e) => updateRow(previewFile.rowData.id, "prestationHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                                  <div className="col-6">
                                                       <label className="form-label fw-bold fs-8 text-gray-600">TVA (€)</label>
                                                       <input
                                                            type="number"
                                                            className="form-control form-control-solid border fw-bold"
                                                            value={previewFile.rowData.prestationVat ?? ""}
                                                            onChange={(e) => updateRow(previewFile.rowData.id, "prestationVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                             </div>
                                        </div>

                                        {/* Section Frais */}
                                        <div className="bg-light-warning bg-opacity-10 p-5 rounded border border-warning border-dashed">
                                             <h5 className="text-warning fs-7 fw-bolder text-uppercase mb-4">Détails Frais</h5>
                                             <div className="row g-5">
                                                  <div className="col-6">
                                                       <label className="form-label fw-bold fs-8 text-gray-600">Frais HT (€)</label>
                                                       <input
                                                            type="number"
                                                            className="form-control form-control-solid border fw-bold"
                                                            value={previewFile.rowData.fraisHt ?? ""}
                                                            onChange={(e) => updateRow(previewFile.rowData.id, "fraisHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                                  <div className="col-6">
                                                       <label className="form-label fw-bold fs-8 text-gray-600">TVA Frais (€)</label>
                                                       <input
                                                            type="number"
                                                            className="form-control form-control-solid border fw-bold"
                                                            value={previewFile.rowData.fraisVat ?? ""}
                                                            onChange={(e) => updateRow(previewFile.rowData.id, "fraisVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>

                              {/* Récapitulatif TOTAL (Desktop uniquement) */}
                              <div className="p-8 bg-light border-top d-none d-lg-block">
                                   <div className="d-flex align-items-center justify-content-between bg-white p-4 rounded border border-primary border-dashed">
                                        <span className="fw-bolder text-gray-700 fs-8 text-uppercase">Total HT Combiné</span>
                                        <span className="fw-boldest text-primary fs-3">{calculatedTotalHt.toFixed(2)} €</span>
                                   </div>
                              </div>
                         </div>
                    </div>

                    {/* PIED DE PAGE MOBILE (Total + Bouton) */}
                    <div className="p-4 bg-body border-top shadow-lg d-flex d-lg-none flex-column align-items-center gap-4">
                         <div className="d-flex align-items-center bg-light-primary bg-opacity-10 px-6 py-3 rounded-pill border border-primary border-dashed w-100 justify-content-between">
                              <span className="fw-bolder text-gray-700 fs-7 text-uppercase">Total HT Combiné</span>
                              <span className="fw-boldest text-primary fs-2">{calculatedTotalHt.toFixed(2)} €</span>
                         </div>

                         <button className="btn btn-primary fw-bold px-20 btn-lg w-100 shadow-sm" onClick={onClose}>
                              <i className="fas fa-check me-2"></i> Terminer la vérification
                         </button>
                    </div>
               </div>
          </MyModal>
     )
}

export default Step3ExtractionVerificationModal
